<script>
import VvPointsCard from "@/components/crm/VvPointsCard.vue";
import VvCouponCard from "@/components/crm/VvCouponCard.vue";
import VvRedeemCard from "@/components/crm/VvRedeemCard.vue";
import VvNewsCard from "@/components/crm/VvNewsCard.vue";
import VvBannerCarousel from "@/components/profile/VvBannerCarousel.vue";
import Mission from "@/components/profile/Mission";
import VvHealthyEyesGrid from "@/components/profile/VvHealthyEyesGrid.vue";
import ModalLoading from "@/components/other/ModalLoading.vue";

export default {
  name: "VisionVentureHome",
  components: {
    VvPointsCard,
    VvNewsCard,
    Mission,
    VvCouponCard,
    VvRedeemCard,
    ModalLoading,
    VvBannerCarousel,
    VvHealthyEyesGrid
  },
  data() {
    return {
      langs: this.$cookies.isKey("lang") ? this.$cookies.get("lang") : {},
      languageList: this.$store.state.languageList,
      privilegeList: [],
      rewardList: [],
      newsList: [],
      isLanguageSheetOpen: false,
      selectedLanguageId: null,
      previousBodyOverflow: "",
      isLoadingPrivilege: true,
      isLoadingReward: true,
      isLoadingNews: true
    };
  },
  computed: {
    profile() {
      return this.$store.state.shortProfile || {};
    },
    lineProfile() {
      return this.$cookies.get("user_profile_token") || {};
    },
    avatar() {
      return (
        this.lineProfile.pictureUrl ||
        this.profile.image_url ||
        this.handleImageSrc
      );
    },
    displayName() {
      const profileName = `${this.profile.first_name_th || ""} ${
        this.profile.last_name_th || ""
      }`.trim();
      return profileName || this.lineProfile.displayName || "-";
    },
    latestPrivileges() {
      return this.privilegeList.slice(0, 5);
    },
    latestRewards() {
      return this.rewardList.slice(0, 5);
    },
    latestNews() {
      return this.newsList.slice(0, 5);
    },
    showLanguageSwitcher() {
      return this.languageList.length > 1;
    },
    currentLanguage() {
      return (
        this.languageList.find(lang => lang.id == this.langs.lang) ||
        this.languageList[0] ||
        {}
      );
    }
  },
  async created() {
    await this.getLanguageList();
    this.getPrivilegeList();
    this.getRewardList();
    this.getNewsList();
  },
  watch: {
    "$route.path"() {
      if (this.$cookies.isKey("lang")) this.langs = this.$cookies.get("lang");
    },
    isLanguageSheetOpen(value) {
      if (value) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return;
      }
      document.body.style.overflow = this.previousBodyOverflow;
    }
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow;
  },
  methods: {
    async getLanguageList() {
      if (this.$store.state.languageList.length == 0) {
        const res = await this.$store.dispatch("getLanguage");
        this.languageList = res;
        return;
      }
      this.languageList = this.$store.state.languageList;
    },
    openLanguageSheet() {
      this.selectedLanguageId = this.currentLanguage.id;
      this.isLanguageSheetOpen = true;
    },
    closeLanguageSheet() {
      this.isLanguageSheetOpen = false;
      this.selectedLanguageId = null;
    },
    selectLanguage(id) {
      this.selectedLanguageId = id;
    },
    async confirmLanguage() {
      const isChanged = await this.changeLang(this.selectedLanguageId);
      if (isChanged) this.closeLanguageSheet();
    },
    async changeLang(val) {
      const index = this.languageList.findIndex(el => el.id == val);
      if (index < 0) return false;
      if (this.langs.lang == val) return true;

      this.$refs.modalLoading.show();
      try {
        const selectedLanguage = this.languageList[index];
        const payload = {
          lang: selectedLanguage.id,
          name: selectedLanguage.name,
          flag: selectedLanguage.image_url
        };

        await this.$router.replace({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            lang_id: String(selectedLanguage.id)
          }
        });
        await this.$cookies.set("lang", JSON.stringify(payload));
        this.langs = payload;
        await this.$getLang();
        await this.$store.dispatch("getProvince", false);
        await this.$store.dispatch("refreshUserProfile");
        // Force the whole app (not just this component) to re-render so
        // every $t()-driven label across child components (Mission,
        // VvPointsCard, VvHealthyEyesGrid, ...) picks up the new language —
        // same convention as VvLanguageSwitcher / LanguageSwitcherV2.
        this.emitter.emit("updateView");
        return true;
      } catch (error) {
        this.$logger({
          response: error.response || {
            config: {
              url: this.$route.fullPath,
              method: "LANGUAGE",
              data: JSON.stringify({ lang_id: val })
            }
          },
          action: { id: 3, action: "change VV home language" }
        });
        return false;
      } finally {
        this.$refs.modalLoading.hide();
      }
    },
    async getPrivilegeList() {
      this.isLoadingPrivilege = true;
      try {
        const data = await this.$axios.get(`/api/v1/Privilege/privilege_list`);
        if (data.result == 1) {
          this.privilegeList = data.detail.filter(el => {
            if (!el.prefix && el.coupon_code_type == 3) return false;
            if (el.availability == 1) return true;
            return el.already_use == 0;
          });
        }
      } catch (_) {
        this.$logger({
          response: _?.response || {
            config: _?.config || {
              url: "/api/v1/Privilege/privilege_list",
              method: "GET",
              methods: "GET",
              data: ""
            }
          },
          action: { id: 3, action: "getPrivilegeList API request failed" }
        });
        this.privilegeList = [];
      } finally {
        this.isLoadingPrivilege = false;
      }
    },
    async getRewardList() {
      this.isLoadingReward = true;
      try {
        const data = await this.$axios("/api/v1/user/GetRedeemReward");
        if (data.result == 1) this.rewardList = data.detail || [];
      } catch (_) {
        this.$logger({
          response: _?.response || {
            config: _?.config || {
              url: "/api/v1/user/GetRedeemReward",
              method: "GET",
              methods: "GET",
              data: ""
            }
          },
          action: { id: 3, action: "getRewardList API request failed" }
        });
        this.rewardList = [];
      } finally {
        this.isLoadingReward = false;
      }
    },
    async getNewsList() {
      this.isLoadingNews = true;
      try {
        const data = await this.$axios.post(`/api/v1/MonthlyPrivilege/list`, {
          page: 1,
          take: 5,
          id: []
        });
        if (data.result == 1) this.newsList = data.detail.detail || [];
      } catch (_) {
        this.$logger({
          response: _?.response || {
            config: _?.config || {
              url: "/api/v1/MonthlyPrivilege/list",
              method: "POST",
              methods: "POST",
              data: ""
            }
          },
          action: { id: 3, action: "getNewsList API request failed" }
        });
        this.newsList = [];
      } finally {
        this.isLoadingNews = false;
      }
    },
    goPrivilege(item) {
      this.$router.push(
        `/privilege/${item.promotion_id}?user_coupon_id=${item.user_coupon_id}`
      );
    },
    goReward(item) {
      this.$router.push(`/reward/${item.id}?type=${item.product_type}&o_id=0`);
    },
    goNews(item) {
      this.$router.push(`/news/${item.id}`);
    }
  }
};
</script>

<template>
  <div class="vv-home" data-test="vv-home">
    <section class="vv-home__top" data-test="vv-home-top">
      <div class="vv-home__user">
        <c-img-lazy :src="avatar" class="vv-home__avatar" />
        <span class="vv-home__name">{{ displayName }}</span>
      </div>
      <div
        v-if="showLanguageSwitcher"
        class="vv-home__language"
        data-test="vv-home-language"
      >
        <button
          type="button"
          class="vv-home__language-btn"
          data-test="vv-home-language-btn"
          :aria-label="$t('language')"
          @click="openLanguageSheet"
        >
          <span
            class="flag-lang vv-home__language-flag"
            data-test="vv-home-language-current-flag"
          >
            {{ currentLanguage.image_url }}
          </span>
          <span>{{ currentLanguage.display_name }}</span>
          <font-awesome-icon icon="chevron-down" />
        </button>
      </div>
    </section>

    <div class="vv-home__banner-full" data-test="vv-home-banner">
      <VvBannerCarousel
        :profile="profile"
        data-test-prefix="vv-home-banner-carousel"
      />
    </div>

    <!-- Floating points card: overlaps banner bottom (Figma: absolute y=352) -->
    <div class="vv-home__points-floating">
      <VvPointsCard :profile="profile" data-test="vv-home-points" />
    </div>

    <section
      class="vv-home__section"
      data-test="vv-home-eye-record-wrapper"
    >
      <VvHealthyEyesGrid />
    </section>

    <section
      class="vv-home__section"
      id="mission"
      data-test="vv-home-mission-section"
    >
      <Mission v-if="displayMission" />
    </section>

    <section
      class="vv-home__section"
      v-if="latestPrivileges.length !== 0"
      data-test="vv-home-privilege-section"
    >
      <div class="vv-home__section-header">
        <h2>{{ $t("privilege") }}</h2>
        <router-link
          to="/privilege"
          class="v2__all"
          data-test="vv-home-privilege-see-all"
        >
          {{ $t("see_all") }}
          <font-awesome-icon icon="chevron-right"></font-awesome-icon>
        </router-link>
      </div>
      <div
        v-if="isLoadingPrivilege"
        class="vv-home__state"
        data-test="vv-home-privilege-loading"
      >
        {{ $t("loading") }}
      </div>

      <!--
        TODO Vision Venture: ticket-notch shape (Figma BooleanOperation)
        ปัจจุบันใช้ VvCouponCard เดิม (rounded rect) — ต่อไปควรมี
        VvCouponCard.vue ที่ใช้ SVG mask หรือ clip-path ตัด notch ตาม design
      -->
      <div v-else class="vv-home__horizontal-list">
        <VvCouponCard
          v-for="item in latestPrivileges"
          :key="item.user_coupon_id || item.promotion_id"
          :item="item"
          compact
          :data-test="`vv-home-privilege-card-${
            item.user_coupon_id || item.promotion_id
          }`"
          @select="goPrivilege"
          @use="goPrivilege"
        />
      </div>
    </section>

    <section
      class="vv-home__section"
      v-if="displayUpcomming && latestRewards.length !== 0"
      data-test="vv-home-reward-section"
    >
      <div class="vv-home__section-header">
        <h2>{{ $t("reward_redeem_now_title") }}</h2>
        <router-link
          to="/reward"
          class="v2__all"
          data-test="vv-home-reward-see-all"
        >
          {{ $t("see_all") }}
          <font-awesome-icon icon="chevron-right"></font-awesome-icon>
        </router-link>
      </div>
      <div
        v-if="isLoadingReward"
        class="vv-home__state"
        data-test="vv-home-reward-loading"
      >
        {{ $t("loading") }}
      </div>

      <div v-else class="vv-home__horizontal-list">
        <VvRedeemCard
          v-for="item in latestRewards"
          :key="item.id"
          :item="item"
          :data-test="`vv-home-reward-card-${item.id}`"
          @select="goReward"
        />
      </div>
    </section>

    <section
      class="vv-home__section"
      v-if="latestNews.length > 0"
      data-test="vv-home-news-section"
    >
      <div class="vv-home__section-header">
        <h2>{{ $t("news") }}</h2>
        <router-link
          to="/news"
          class="v2__all"
          data-test="vv-home-news-see-all"
        >
          {{ $t("see_all") }}
          <font-awesome-icon icon="chevron-right"></font-awesome-icon>
        </router-link>
      </div>
      <div
        v-if="isLoadingNews"
        class="vv-home__state"
        data-test="vv-home-news-loading"
      >
        {{ $t("loading") }}
      </div>

      <div v-else class="vv-home__news-list">
        <VvNewsCard
          v-for="item in latestNews"
          :key="item.id"
          :item="item"
          :data-test="`vv-home-news-card-${item.id}`"
          @select="goNews"
        />
      </div>
    </section>

    <Teleport to="body">
      <div
        v-if="isLanguageSheetOpen"
        class="vv-language-modal"
        role="presentation"
        data-test="vv-home-language-modal"
        @click.self="closeLanguageSheet"
      >
        <section
          class="vv-language-modal__sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vv-language-modal-title"
        >
          <h2 id="vv-language-modal-title" class="vv-language-modal__title">
            {{ $t("language") }}
          </h2>

          <div class="vv-language-modal__list" data-test="vv-home-language-list">
            <button
              v-for="lang in languageList"
              :key="'vv-language-sheet-' + lang.id"
              type="button"
              class="vv-language-modal__item"
              :class="{
                'vv-language-modal__item--selected':
                  lang.id == selectedLanguageId
              }"
              :aria-pressed="lang.id == selectedLanguageId"
              :data-test="`vv-home-language-item-${lang.id}`"
              @click="selectLanguage(lang.id)"
            >
              <span
                class="flag-lang vv-language-modal__flag"
                :data-test="`vv-home-language-flag-${lang.id}`"
              >
                {{ lang.image_url }}
              </span>
              <span>{{ lang.display_name }}</span>
              <font-awesome-icon
                v-if="lang.id == selectedLanguageId"
                icon="check-circle"
                class="vv-language-modal__check"
              />
            </button>
          </div>

          <div class="vv-language-modal__actions">
            <button
              type="button"
              class="vv-language-modal__confirm"
              data-test="vv-home-language-modal-confirm-btn"
              @click="confirmLanguage"
            >
              {{ $t("vv_language_confirm") }}
            </button>
            <button
              type="button"
              class="vv-language-modal__cancel"
              data-test="vv-home-language-modal-cancel-btn"
              @click="closeLanguageSheet"
            >
              {{ $t("vv_language_cancel") }}
            </button>
          </div>
        </section>
      </div>
    </Teleport>

    <ModalLoading ref="modalLoading" data-test="vv-home-language-loading-modal" />
  </div>
</template>

<style lang="scss" scoped>
/*
  Vision Venture Home
  - โทนสี/ฟอนต์: ใช้ token จาก src/style.css + src/assets/stylesheets/typography.css
  - radius/spacing: อิงจาก Figma frame 1:10656 (16/24 scale, section gap 24)
  - glass surface: rgba(255,255,255,*) + backdrop-filter (Figma effect)
*/

/* Gradient background อยู่ที่ VisionVentureLayout แล้ว
   หน้านี้จัดแค่ padding + section spacing */
.vv-home {
  /* Figma point card uses two frosted layers above the banner:
     the upper half keeps the banner's blue tint and blur, while the lower
     half is a lighter glass surface.  Resolve both from API theme tokens;
     this local override is intentionally limited to the VV Home. */
  --crm-card-glass-background: color-mix(
    in srgb,
    var(--theme-background-color) 76%,
    transparent
  );
  --vv-points-card-background: transparent;
  --vv-points-card-top-background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--theme-background-color) 44%, transparent) 0%,
    color-mix(
        in srgb,
        var(--theme-background-color) 36%,
        transparent
      )
      100%
  );
  --vv-points-card-bottom-background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--theme-background-color) 76%, transparent) 0%,
    color-mix(in srgb, var(--theme-background-color) 82%, transparent) 100%
  );
  --crm-card-glass-box-shadow: 0 4px 4px
    var(--theme-secondary-color-low);

  position: relative;
  min-height: 100%;
  padding: 0 1rem 1rem;
}

.vv-home :deep(.vv-points-card),
.vv-home :deep(.vv-redeem-card),
.vv-home :deep(.vv-news-card),
.vv-home :deep(.card-mission--vv) {
  box-shadow: 0 4px 4px var(--theme-secondary-color-low);
}

.vv-home :deep(.vv-coupon-card) {
  filter: drop-shadow(0 4px 4px var(--theme-secondary-color-low));
}

.vv-home__top,
.vv-home__user,
.vv-home__section-header {
  display: flex;
  align-items: center;
}

.vv-home__top {
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
}

.vv-home__user {
  min-width: 0;
  gap: 8px;
}

.vv-home__avatar {
  width: 34px;
  height: 34px;
  overflow: hidden;
  border-radius: 50%;
}

.vv-home__name {
  overflow: hidden;
  max-width: 160px;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-color);
  font-size: var(--font-body);
  font-weight: 700;
}

.vv-home__language {
  flex: 0 0 auto;
}

.vv-home__language-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 6px 12px;
  border: var(--crm-card-glass-border);
  border-radius: 999px;
  background: var(--crm-card-glass-background);
  backdrop-filter: blur(12px);
  color: var(--text-color);
  font-size: var(--font-helper);
  font-weight: 700;
}

.vv-home__language-flag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.vv-language-modal {
  position: fixed;
  z-index: 2000;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: var(--crm-vv-loading-overlay);
}

.vv-language-modal__sheet {
  width: min(100%, 430px);
  padding: 18px 16px max(16px, env(safe-area-inset-bottom));
  border-radius: var(--crm-vv-register-sheet-radius)
    var(--crm-vv-register-sheet-radius) 0 0;
  background: var(--crm-vv-register-surface);
}

.vv-language-modal__title {
  margin: 0 0 12px;
  color: var(--crm-vv-register-text);
  font-size: var(--font-title-sm);
  font-weight: 600;
  text-align: center;
}

.vv-language-modal__list {
  display: grid;
  gap: 2px;
  padding-bottom: 14px;
}

.vv-language-modal__item {
  display: grid;
  width: 100%;
  min-height: 44px;
  grid-template-columns: 26px 1fr 24px;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 0;
  border-radius: var(--crm-border-radius);
  background: transparent;
  color: var(--crm-vv-register-text);
  font-size: var(--font-body);
  text-align: left;
}

.vv-language-modal__flag {
  display: inline-flex;
  width: 26px;
  height: 20px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  line-height: 1;
}

.vv-language-modal__check {
  justify-self: center;
  color: var(--primary-color);
}

.vv-language-modal__actions {
  display: grid;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--crm-border-color);
}

.vv-language-modal__confirm,
.vv-language-modal__cancel {
  width: 100%;
  min-height: 44px;
  border-radius: var(--crm-border-radius);
  font-size: var(--font-body);
  font-weight: 600;
}

.vv-language-modal__confirm {
  border: 1px solid var(--button-color);
  background: var(--button-color);
  color: var(--font-button-color);
}

.vv-language-modal__cancel {
  border: 1px solid var(--crm-border-color);
  background: var(--crm-vv-register-surface);
  color: var(--crm-vv-register-muted-text);
}

.vv-home__banner-full {
  margin: 0 -1rem;
  overflow: hidden;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

/* Floating overlap: Figma shows Points Card sitting on top of banner bottom */
.vv-home__points-floating {
  position: relative;
  margin-top: -74px;
  z-index: 2;
}

.vv-home__glass {
  border-radius: 24px;
  border: var(--crm-card-glass-border);
  background: var(--crm-card-glass-background);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: var(--crm-card-glass-box-shadow);
}

.vv-home__glass--points {
  padding: 12px;
}

.vv-home__section {
  margin-top: 24px;
}

.vv-home__section-header {
  justify-content: space-between;
  margin-bottom: 12px;
}

.vv-home__section-header h2 {
  margin: 0;
  color: var(--text-color);
  font-size: var(--font-title-sm);
  font-weight: 900;
}

.vv-home__horizontal-list {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.vv-home__horizontal-list::-webkit-scrollbar {
  display: none;
}

.vv-home__news-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.vv-home__state {
  padding: 18px 12px;
  border-radius: 16px;
  background: var(--crm-card-glass-background);
  backdrop-filter: blur(12px);
  color: var(--tab-text-color);
  low-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.standard-v2-home__horizontal-list::-webkit-scrollbar {
  display: none;
}

.standard-v2-reward__redeem-now-list {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 0 0px 4px;
  scrollbar-width: none;
}

.standard-v2-reward__redeem-now-list::-webkit-scrollbar {
  display: none;
}

.standard-v2-home__state {
  padding: 18px 12px;
  border-radius: 12px;
  background: var(--secondary-color);
  color: var(--tab-text-color);
  w-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.standard-v2-home__horizontal-list::-webkit-scrollbar {
  display: none;
}

.standard-v2-reward__redeem-now-list {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 0 0px 4px;
  scrollbar-width: none;
}

.standard-v2-reward__redeem-now-list::-webkit-scrollbar {
  display: none;
}

.standard-v2-home__state {
  padding: 18px 12px;
  border-radius: 12px;
  background: var(--secondary-color);
  color: var(--tab-text-color);
  text-align: center;
}
</style>
